<template>
  <div id="printable" class="summary-container">
    <!-- Header -->
    <div class="header">
      <div class="facility-name">
        TUURU COTTOLENGO HEALTH CENTRE - AR2026259
      </div>
      <div class="facility-details">
        <div>Igembe Central</div>
        <div>P.O BOX 77 - Meru | Tel: 254254746758069</div>
        <div>Email: suore.tuuru@cottolengo.org</div>
      </div>
      <div class="register-title">MOH 711 - COMMODITY CONSUMPTION REPORT</div>
    </div>

    <!-- Month/Year and Generated By Bar -->
    <div class="info-bar">
      <div class="info-left">
        <div class="info-item">
          <span class="info-label">Month:</span>
          <select v-model="selectedMonth" class="month-select">
            <option value="January">January</option>
            <option value="February">February</option>
            <option value="March">March</option>
            <option value="April">April</option>
            <option value="May">May</option>
            <option value="June">June</option>
            <option value="July">July</option>
            <option value="August">August</option>
            <option value="September">September</option>
            <option value="October">October</option>
            <option value="November">November</option>
            <option value="December">December</option>
          </select>
        </div>
        <div class="info-item">
          <span class="info-label">Year:</span>
          <input
            type="number"
            v-model="selectedYear"
            class="year-input"
            min="2020"
            max="2030"
          />
        </div>
        <div class="info-item">
          <span class="info-label">Generated By:</span>
          <span class="info-value">{{ generatedBy }}</span>
        </div>
      </div>
      <button @click="printPage" class="btn-print">Print Report</button>
    </div>

    <!-- Period Display for Print -->
    <div class="print-period">
      <strong>Reporting Period:</strong> {{ selectedMonth }} {{ selectedYear }}
    </div>

    <!-- Summary Statistics -->
    <div class="summary-stats">
      <div class="stat-box">
        <span class="stat-label">Total Commodities</span>
        <span class="stat-number">{{ totalCommodities }}</span>
      </div>
      <div class="stat-box">
        <span class="stat-label">Items with Stockout</span>
        <span class="stat-number">{{ stockoutCount }}</span>
      </div>
      <div class="stat-box">
        <span class="stat-label">Value Consumed (KSh)</span>
        <span class="stat-number">{{
          totalValueConsumed.toLocaleString()
        }}</span>
      </div>
      <div class="stat-box">
        <span class="stat-label">Expiring Items</span>
        <span class="stat-number">{{ expiringCount }}</span>
      </div>
    </div>

    <!-- Section 1: Essential Medicines Consumption -->
    <div class="section">
      <div class="section-title">1. ESSENTIAL MEDICINES CONSUMPTION</div>
      <div class="table-wrapper">
        <table class="commodity-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Commodity Name</th>
              <th>Formulation</th>
              <th>Unit</th>
              <th>Opening Stock</th>
              <th>Received</th>
              <th>Consumed</th>
              <th>Losses</th>
              <th>Closing Stock</th>
              <th>Stockout Days</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in essentialMedicines" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td class="text-center">{{ item.formulation }}</td>
              <td class="text-center">{{ item.unit }}</td>
              <td class="text-center">{{ item.openingStock }}</td>
              <td class="text-center">{{ item.received }}</td>
              <td class="text-center">
                <strong>{{ item.consumed }}</strong>
              </td>
              <td class="text-center">{{ item.losses }}</td>
              <td class="text-center">{{ item.closingStock }}</td>
              <td class="text-center">{{ item.stockoutDays }}</td>
              <td class="text-center">
                <span :class="getStatusClass(item.status)">{{
                  item.status
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 2: Laboratory Commodities Consumption -->
    <div class="section">
      <div class="section-title">2. LABORATORY COMMODITIES CONSUMPTION</div>
      <div class="table-wrapper">
        <table class="commodity-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Commodity Name</th>
              <th>Unit</th>
              <th>Opening Stock</th>
              <th>Received</th>
              <th>Consumed</th>
              <th>Losses</th>
              <th>Closing Stock</th>
              <th>Stockout Days</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in laboratoryCommodities" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td class="text-center">{{ item.unit }}</td>
              <td class="text-center">{{ item.openingStock }}</td>
              <td class="text-center">{{ item.received }}</td>
              <td class="text-center">
                <strong>{{ item.consumed }}</strong>
              </td>
              <td class="text-center">{{ item.losses }}</td>
              <td class="text-center">{{ item.closingStock }}</td>
              <td class="text-center">{{ item.stockoutDays }}</td>
              <td class="text-center">
                <span :class="getStatusClass(item.status)">{{
                  item.status
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 3: HIV/ART Commodities -->
    <div class="section">
      <div class="section-title">3. HIV/ART COMMODITIES</div>
      <div class="table-wrapper">
        <table class="commodity-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Commodity Name</th>
              <th>Formulation</th>
              <th>Unit</th>
              <th>Opening Stock</th>
              <th>Received</th>
              <th>Consumed</th>
              <th>Losses</th>
              <th>Closing Stock</th>
              <th>Stockout Days</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in hivCommodities" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td class="text-center">{{ item.formulation }}</td>
              <td class="text-center">{{ item.unit }}</td>
              <td class="text-center">{{ item.openingStock }}</td>
              <td class="text-center">{{ item.received }}</td>
              <td class="text-center">
                <strong>{{ item.consumed }}</strong>
              </td>
              <td class="text-center">{{ item.losses }}</td>
              <td class="text-center">{{ item.closingStock }}</td>
              <td class="text-center">{{ item.stockoutDays }}</td>
              <td class="text-center">
                <span :class="getStatusClass(item.status)">{{
                  item.status
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 4: Malaria Commodities -->
    <div class="section">
      <div class="section-title">4. MALARIA COMMODITIES</div>
      <div class="table-wrapper">
        <table class="commodity-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Commodity Name</th>
              <th>Formulation</th>
              <th>Unit</th>
              <th>Opening Stock</th>
              <th>Received</th>
              <th>Consumed</th>
              <th>Losses</th>
              <th>Closing Stock</th>
              <th>Stockout Days</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in malariaCommodities" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td class="text-center">{{ item.formulation }}</td>
              <td class="text-center">{{ item.unit }}</td>
              <td class="text-center">{{ item.openingStock }}</td>
              <td class="text-center">{{ item.received }}</td>
              <td class="text-center">
                <strong>{{ item.consumed }}</strong>
              </td>
              <td class="text-center">{{ item.losses }}</td>
              <td class="text-center">{{ item.closingStock }}</td>
              <td class="text-center">{{ item.stockoutDays }}</td>
              <td class="text-center">
                <span :class="getStatusClass(item.status)">{{
                  item.status
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 5: TB Commodities -->
    <div class="section">
      <div class="section-title">5. TB COMMODITIES</div>
      <div class="table-wrapper">
        <table class="commodity-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Commodity Name</th>
              <th>Formulation</th>
              <th>Unit</th>
              <th>Opening Stock</th>
              <th>Received</th>
              <th>Consumed</th>
              <th>Losses</th>
              <th>Closing Stock</th>
              <th>Stockout Days</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in tbCommodities" :key="index">
              <td class="text-center">{{ index + 1 }}</td>
              <td>{{ item.name }}</td>
              <td class="text-center">{{ item.formulation }}</td>
              <td class="text-center">{{ item.unit }}</td>
              <td class="text-center">{{ item.openingStock }}</td>
              <td class="text-center">{{ item.received }}</td>
              <td class="text-center">
                <strong>{{ item.consumed }}</strong>
              </td>
              <td class="text-center">{{ item.losses }}</td>
              <td class="text-center">{{ item.closingStock }}</td>
              <td class="text-center">{{ item.stockoutDays }}</td>
              <td class="text-center">
                <span :class="getStatusClass(item.status)">{{
                  item.status
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 6: Summary of Key Indicators -->
    <div class="section">
      <div class="section-title">6. KEY INDICATORS SUMMARY</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Stock Availability Rate</div>
          <div class="stat-value">{{ keyIndicators.stockAvailability }}%</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Order Fill Rate</div>
          <div class="stat-value">{{ keyIndicators.orderFillRate }}%</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Inventory Turnover (Months)</div>
          <div class="stat-value">{{ keyIndicators.inventoryTurnover }}</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-label">Wastage Rate (%)</div>
          <div class="stat-value">{{ keyIndicators.wastageRate }}%</div>
        </div>
      </div>
    </div>

    <!-- Section 7: Expiry Alert -->
    <div class="section">
      <div class="section-title">
        7. EXPIRY ALERT (Items expiring in next 3 months)
      </div>
      <div class="table-wrapper" v-if="expiryAlerts.length > 0">
        <table class="data-table">
          <thead>
            <tr>
              <th>Commodity Name</th>
              <th>Batch Number</th>
              <th>Expiry Date</th>
              <th>Quantity Remaining</th>
              <th>Action Required</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="alert in expiryAlerts" :key="alert.batchNo">
              <td>{{ alert.name }}</td>
              <td class="text-center">{{ alert.batchNo }}</td>
              <td class="text-center">{{ alert.expiryDate }}</td>
              <td class="text-center">{{ alert.quantity }}</td>
              <td class="text-center">
                <span class="warning-badge">{{ alert.action }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="no-alerts">
        No items expiring in the next 3 months.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MOH711Commodity",
  data() {
    return {
      selectedMonth: "April",
      selectedYear: 2026,
      generatedBy: "SR. JANE KAREGI MUNYUGI",

      essentialMedicines: [
        {
          name: "Paracetamol",
          formulation: "500mg Tab",
          unit: "Tablet",
          openingStock: 5000,
          received: 2000,
          consumed: 1850,
          losses: 50,
          closingStock: 5100,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Amoxicillin",
          formulation: "500mg Cap",
          unit: "Capsule",
          openingStock: 3000,
          received: 1500,
          consumed: 1200,
          losses: 30,
          closingStock: 3270,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Artemether/Lumefantrine",
          formulation: "20/120mg Tab",
          unit: "Tablet",
          openingStock: 800,
          received: 500,
          consumed: 850,
          losses: 10,
          closingStock: 440,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Metformin",
          formulation: "500mg Tab",
          unit: "Tablet",
          openingStock: 2000,
          received: 1000,
          consumed: 950,
          losses: 20,
          closingStock: 2030,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Lisinopril",
          formulation: "10mg Tab",
          unit: "Tablet",
          openingStock: 1500,
          received: 800,
          consumed: 780,
          losses: 10,
          closingStock: 1510,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "ORS Sachets",
          formulation: "Powder",
          unit: "Sachet",
          openingStock: 2000,
          received: 500,
          consumed: 1200,
          losses: 50,
          closingStock: 1250,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Zinc Sulfate",
          formulation: "20mg Tab",
          unit: "Tablet",
          openingStock: 500,
          received: 200,
          consumed: 400,
          losses: 10,
          closingStock: 290,
          stockoutDays: 5,
          status: "Low Stock",
        },
        {
          name: "Ciprofloxacin",
          formulation: "250mg Tab",
          unit: "Tablet",
          openingStock: 400,
          received: 300,
          consumed: 450,
          losses: 10,
          closingStock: 240,
          stockoutDays: 3,
          status: "Low Stock",
        },
      ],

      laboratoryCommodities: [
        {
          name: "Malaria RDT Kits",
          unit: "Kit",
          openingStock: 500,
          received: 300,
          consumed: 480,
          losses: 10,
          closingStock: 310,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "HIV Rapid Test Kits",
          unit: "Kit",
          openingStock: 600,
          received: 400,
          consumed: 550,
          losses: 15,
          closingStock: 435,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Pregnancy Test Kits",
          unit: "Kit",
          openingStock: 200,
          received: 150,
          consumed: 180,
          losses: 5,
          closingStock: 165,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Urine Dipsticks",
          unit: "Strip",
          openingStock: 1000,
          received: 500,
          consumed: 900,
          losses: 20,
          closingStock: 580,
          stockoutDays: 2,
          status: "Low Stock",
        },
        {
          name: "Hb Meter Strips",
          unit: "Strip",
          openingStock: 800,
          received: 400,
          consumed: 750,
          losses: 15,
          closingStock: 435,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Blood Glucose Strips",
          unit: "Strip",
          openingStock: 600,
          received: 300,
          consumed: 680,
          losses: 10,
          closingStock: 210,
          stockoutDays: 5,
          status: "Low Stock",
        },
      ],

      hivCommodities: [
        {
          name: "TDF/3TC/DTG (TLD)",
          formulation: "Tab",
          unit: "Tablet",
          openingStock: 3000,
          received: 2000,
          consumed: 2800,
          losses: 30,
          closingStock: 2170,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "AZT/3TC/NVP",
          formulation: "Tab",
          unit: "Tablet",
          openingStock: 1500,
          received: 800,
          consumed: 1200,
          losses: 20,
          closingStock: 1080,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Cotrimoxazole",
          formulation: "480mg Tab",
          unit: "Tablet",
          openingStock: 2000,
          received: 1000,
          consumed: 1900,
          losses: 25,
          closingStock: 1075,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "NVP Syrup",
          formulation: "Liquid",
          unit: "Bottle",
          openingStock: 100,
          received: 50,
          consumed: 80,
          losses: 2,
          closingStock: 68,
          stockoutDays: 7,
          status: "Low Stock",
        },
      ],

      malariaCommodities: [
        {
          name: "ACT (AL) Adult",
          formulation: "20/120mg",
          unit: "Tablet",
          openingStock: 800,
          received: 500,
          consumed: 850,
          losses: 10,
          closingStock: 440,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "ACT (AL) Paediatric",
          formulation: "10/20mg",
          unit: "Tablet",
          openingStock: 600,
          received: 400,
          consumed: 550,
          losses: 8,
          closingStock: 442,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "SP (Fansidar)",
          formulation: "500/25mg",
          unit: "Tablet",
          openingStock: 400,
          received: 200,
          consumed: 350,
          losses: 5,
          closingStock: 245,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Injectable Artesunate",
          formulation: "60mg",
          unit: "Vial",
          openingStock: 50,
          received: 30,
          consumed: 45,
          losses: 1,
          closingStock: 34,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Quinine Injection",
          formulation: "300mg/ml",
          unit: "Ampoule",
          openingStock: 100,
          received: 50,
          consumed: 80,
          losses: 2,
          closingStock: 68,
          stockoutDays: 0,
          status: "Adequate",
        },
      ],

      tbCommodities: [
        {
          name: "RHZE (Rifampicin/Isoniazid/Pyrazinamide/Ethambutol)",
          formulation: "Tab",
          unit: "Tablet",
          openingStock: 1000,
          received: 500,
          consumed: 850,
          losses: 10,
          closingStock: 640,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "RH (Rifampicin/Isoniazid)",
          formulation: "Tab",
          unit: "Tablet",
          openingStock: 800,
          received: 400,
          consumed: 700,
          losses: 8,
          closingStock: 492,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Ethambutol",
          formulation: "400mg Tab",
          unit: "Tablet",
          openingStock: 500,
          received: 300,
          consumed: 450,
          losses: 5,
          closingStock: 345,
          stockoutDays: 0,
          status: "Adequate",
        },
        {
          name: "Pyrazinamide",
          formulation: "500mg Tab",
          unit: "Tablet",
          openingStock: 500,
          received: 300,
          consumed: 420,
          losses: 5,
          closingStock: 375,
          stockoutDays: 0,
          status: "Adequate",
        },
      ],

      keyIndicators: {
        stockAvailability: 87.5,
        orderFillRate: 92.3,
        inventoryTurnover: 1.8,
        wastageRate: 2.1,
      },

      expiryAlerts: [
        {
          name: "Artemether/Lumefantrine",
          batchNo: "B12345",
          expiryDate: "2026-06-15",
          quantity: 240,
          action: "Use first / Transfer",
        },
        {
          name: "Malaria RDT Kits",
          batchNo: "R67890",
          expiryDate: "2026-07-20",
          quantity: 85,
          action: "Use first",
        },
      ],
    };
  },
  computed: {
    totalCommodities() {
      const all = [
        ...this.essentialMedicines,
        ...this.laboratoryCommodities,
        ...this.hivCommodities,
        ...this.malariaCommodities,
        ...this.tbCommodities,
      ];
      return all.length;
    },
    stockoutCount() {
      const all = [
        ...this.essentialMedicines,
        ...this.laboratoryCommodities,
        ...this.hivCommodities,
        ...this.malariaCommodities,
        ...this.tbCommodities,
      ];
      return all.filter(
        (i) => i.status === "Low Stock" || i.status === "Stockout",
      ).length;
    },
    totalValueConsumed() {
      return 185000;
    },
    expiringCount() {
      return this.expiryAlerts.length;
    },
  },
  methods: {
    getStatusClass(status) {
      if (status === "Adequate") return "status-good";
      if (status === "Low Stock") return "status-low";
      return "status-out";
    },
    printPage() {
      window.print();
    },
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.summary-container {
  background: white;
  min-height: 100vh;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  text-align: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #1a56db;
}

.facility-name {
  font-size: 20px;
  font-weight: bold;
  color: #1a56db;
  margin-bottom: 6px;
}

.facility-details {
  font-size: 11px;
  color: #4b5563;
  line-height: 1.4;
  margin-bottom: 10px;
}

.register-title {
  font-size: 14px;
  font-weight: bold;
  background: #f3f4f6;
  display: inline-block;
  padding: 5px 16px;
  border-radius: 20px;
  color: #1f2937;
}

.info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.info-left {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.info-value {
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
}

.month-select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  background: white;
}

.year-input {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  width: 70px;
}

.btn-print {
  padding: 8px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.btn-print:hover {
  background: #2563eb;
}

.print-period {
  text-align: center;
  padding: 8px;
  margin-bottom: 20px;
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  font-size: 12px;
  color: #92400e;
  border-radius: 6px;
}

.summary-stats {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.stat-box {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border-radius: 12px;
  padding: 12px 20px;
  text-align: center;
  min-width: 170px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #bfdbfe;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.stat-number {
  display: block;
  font-size: 26px;
  font-weight: bold;
  color: white;
}

.section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 14px;
  font-weight: bold;
  color: #1f2937;
  background: #e5e7eb;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 500px;
  overflow-y: auto;
}

.commodity-table,
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  min-width: 1000px;
}

.commodity-table th,
.data-table th {
  background: #1f2937;
  color: white;
  padding: 8px 6px;
  font-weight: 600;
  text-align: center;
  border: 1px solid #374151;
  position: sticky;
  top: 0;
  z-index: 10;
}

.commodity-table td,
.data-table td {
  border: 1px solid #d1d5db;
  padding: 6px 4px;
  text-align: left;
}

.commodity-table td.text-center,
.data-table td.text-center {
  text-align: center;
}

.status-good {
  background: #d1fae5;
  color: #065f46;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: inline-block;
}

.status-low {
  background: #fef3c7;
  color: #92400e;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: inline-block;
}

.status-out {
  background: #fee2e2;
  color: #dc2626;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: inline-block;
}

.warning-badge {
  background: #fef3c7;
  color: #92400e;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: inline-block;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 10px;
}

.stat-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
  text-align: center;
}

.stat-card.warning {
  background: #fef3c7;
  border-color: #fde68a;
}

.stat-card.warning .stat-value {
  color: #d97706;
}

.stat-card .stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #059669;
}

.stat-card.warning .stat-label {
  color: #92400e;
}

.stat-card .stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.no-alerts {
  text-align: center;
  padding: 20px;
  background: #f3f4f6;
  border-radius: 8px;
  color: #6b7280;
  font-size: 12px;
}

@media print {
  @page {
    size: landscape;
    margin: 0.3cm;
  }

  .btn-print,
  .month-select,
  .year-input {
    display: none;
  }

  .info-bar {
    display: none;
  }

  .print-period {
    background: #fef3c7;
    margin: 10px 0;
    padding: 5px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .summary-stats {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .summary-container {
    padding: 0.2cm;
  }

  .header {
    margin-bottom: 5px;
  }

  .facility-name {
    font-size: 14px;
  }

  .facility-details {
    font-size: 8px;
  }

  .register-title {
    font-size: 10px;
  }

  .section-title {
    font-size: 11px;
    padding: 4px 8px;
  }

  .table-wrapper {
    overflow: visible !important;
    max-height: none;
  }

  .commodity-table,
  .data-table {
    font-size: 8px;
    min-width: 0 !important;
  }

  .commodity-table th,
  .data-table th {
    padding: 4px 3px;
    font-size: 8px;
    background: #e5e7eb !important;
    color: black !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .commodity-table td,
  .data-table td {
    padding: 4px 3px;
    font-size: 7.5px;
  }

  .stat-card {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
